import { createModelPickerPlugin } from '#pplx/frontend/apps/ask/composer-sdk-plugins/model-selector/modelPickerPlugin.js';
import { createSearchModeTogglePlugin } from '#pplx/frontend/apps/ask/composer-sdk-plugins/search-mode-toggle/SearchModeTogglePlugin.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useCallback, useMemo } from 'react';
const computerCommandMenuOpenedEvent = EVENT_PAYLOAD;
/** Composes Ask mode and model controls from caller-owned policy and selection. */
export function useAskSearchModePlugins({ appearance, canClearSearchMode, computerCommandMenu, computerEffort, computerModelChipModelOverride, hasImageAttachments, isFollowUp, onFocus: onAfterSelect, onSearchModeCleared, selectSearchModeOverride, onSearchModeSelected, onSearchModelDeselect, onSearchModelSelect, querySource, renderToggle, modelSelectorOverrides, searchModeControls, searchModeOverride, searchModel, searchModelMenuLocalMode, searchModelTriggerAccessory, selectedSearchMode, showComputerModelChip, showLeftAttribution = true, showModelSelector, }) {
    const { track } = useInstrumentation();
    const { enabled, omittedModes, toggleVisibility } = searchModeControls;
    const isBrowserAgentMode = (searchModeOverride ?? selectedSearchMode) === SearchMode.BROWSER_AGENT;
    const handleSearchModeCleared = useCallback((mode) => {
        onSearchModeCleared?.(mode);
        onAfterSelect();
    }, [onAfterSelect, onSearchModeCleared]);
    const handleComputerMenuToggle = useCallback((open) => {
        if (computerCommandMenu === undefined)
            return;
        if (open && !computerCommandMenu.open) {
            track(computerCommandMenuOpenedEvent({
                name: 'computer command menu opened',
                data: { source: 'toggle' },
            }));
        }
        computerCommandMenu.onOpenChange(open);
    }, [computerCommandMenu, track]);
    const computerMenuToggle = useMemo(() => computerCommandMenu === undefined
        ? undefined
        : { open: computerCommandMenu.open, onOpenChange: handleComputerMenuToggle }, [computerCommandMenu, handleComputerMenuToggle]);
    const modePlugin = useMemo(() => showLeftAttribution && (enabled || isBrowserAgentMode)
        ? createSearchModeTogglePlugin({
            enabled,
            appearance,
            canClearSearchMode,
            computerCommandMenu: computerMenuToggle,
            isFollowUp,
            omittedSearchModes: omittedModes,
            onAfterSelect,
            onSearchModeCleared: handleSearchModeCleared,
            ...(selectSearchModeOverride && { selectSearchModeOverride }),
            onSearchModeSelected,
            renderToggle,
            searchMode: searchModeOverride,
            toggleVisibility,
        })
        : undefined, [
        appearance,
        canClearSearchMode,
        computerMenuToggle,
        enabled,
        handleSearchModeCleared,
        isBrowserAgentMode,
        isFollowUp,
        omittedModes,
        onAfterSelect,
        onSearchModeSelected,
        selectSearchModeOverride,
        renderToggle,
        searchModeOverride,
        showLeftAttribution,
        toggleVisibility,
    ]);
    const modelPlugin = useMemo(() => createModelPickerPlugin({
        ...computerEffort,
        computerModelChipModelOverride,
        isFollowUp,
        localMode: searchModelMenuLocalMode,
        triggerAccessory: searchModelTriggerAccessory,
        onSearchModelDeselect,
        onSearchModelSelect,
        querySource,
        modelSelectorOverrides,
        restrictions: { allowTextOnlyModels: !hasImageAttachments },
        searchModel,
        showComputerModelChip,
        showModelSelector,
    }), [
        computerEffort,
        hasImageAttachments,
        computerModelChipModelOverride,
        isFollowUp,
        onSearchModelDeselect,
        onSearchModelSelect,
        querySource,
        modelSelectorOverrides,
        searchModel,
        searchModelMenuLocalMode,
        searchModelTriggerAccessory,
        showComputerModelChip,
        showModelSelector,
    ]);
    return useMemo(() => [...(modePlugin ? [modePlugin] : []), modelPlugin], [modePlugin, modelPlugin]);
}
//# sourceMappingURL=useAskSearchModePlugins.js.map